import { auth, db, functions } from "./firebaseConfig";
import { httpsCallable } from "firebase/functions";
import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  getDocsFromServer,
  query,
  where,
  updateDoc,
  increment,
} from "firebase/firestore";
import type { Query, QuerySnapshot } from "firebase/firestore";
import { retryAsync } from "../util/retryAsync";
import { saveBlobAsFile } from "../util/downloadFile";
import type {
  Education,
  Level,
  QueryData,
  EducationQueryData,
} from "./firebaseHelper.types";
import { QUESTIONS_PER_SUBTEST_OVERRIDE } from "../constants";
import { isFillInBlankQuestionType } from "./quizQuestionFilters";

export type TheoryInfo = {
  id: string;
  name: string;
  description?: string;
  category?: string;
  sectionTitle?: string;
};

export async function getTheoriesForAppID(appID: string) {
  try {
    // 1) Fetch theories explicitly assigned to this app (legacy path)
    const theoriesRef = collection(db, "theories");
    const directQuery = query(theoriesRef, where("AppID", "==", appID));
    const directSnapshot = await getDocs(directQuery);

    const byAppId: Record<string, any> = {};
    directSnapshot.forEach((docSnap) => {
      const data: any = docSnap.data();
      byAppId[docSnap.id] = { ID: docSnap.id, ...data };
    });

    // 2) Fetch all levels for this app and collect referenced TheoryIDs
    const levels = await getLevelsForAppID(appID);
    const theoryIdToLevelIndex = new Map<string, number>();
    levels.forEach((lvl: any, index: number) => {
      if (lvl.LevelType === "theory" && lvl.TheoryID) {
        const id = String(lvl.TheoryID);
        if (!theoryIdToLevelIndex.has(id)) {
          theoryIdToLevelIndex.set(id, lvl.TotalLevelIndex ?? index);
        }
      }
    });

    const theoryIdsFromLevels = Array.from(theoryIdToLevelIndex.keys());
    const fromLevels = await getObjectsForIDs("theories", theoryIdsFromLevels);

    // 4) Merge and dedupe
    const merged: Record<string, any> = { ...byAppId };
    for (const th of fromLevels) {
      merged[th.ID] = th;
    }
    const theories: any[] = Object.values(merged);

    // 5) Sort: prefer level order (TotalLevelIndex) when available, else CategoryIndex, SectionIndex, Index
    theories.sort((a, b) => {
      const aLevelIdx = theoryIdToLevelIndex.get(String(a?.ID));
      const bLevelIdx = theoryIdToLevelIndex.get(String(b?.ID));

      if (aLevelIdx !== undefined && bLevelIdx !== undefined) {
        return aLevelIdx - bLevelIdx;
      }
      if (aLevelIdx !== undefined) return -1;
      if (bLevelIdx !== undefined) return 1;

      const aCat = Number(a?.CategoryIndex ?? Number.MAX_SAFE_INTEGER);
      const bCat = Number(b?.CategoryIndex ?? Number.MAX_SAFE_INTEGER);
      if (aCat !== bCat) return aCat - bCat;

      const aSec = Number(a?.SectionIndex ?? Number.MAX_SAFE_INTEGER);
      const bSec = Number(b?.SectionIndex ?? Number.MAX_SAFE_INTEGER);
      if (aSec !== bSec) return aSec - bSec;

      const aIdx = Number(a?.Index ?? Number.MAX_SAFE_INTEGER);
      const bIdx = Number(b?.Index ?? Number.MAX_SAFE_INTEGER);
      return aIdx - bIdx;
    });

    return theories;
  } catch (error) {
    console.error("Error fetching theories:", error);
    return [];
  }
}

const LEVELS_FETCH_MAX_ATTEMPTS = 3;

// Resolves the levels snapshot, preferring fresh server data. The server is
// retried a few times to ride out transient connectivity glitches; only when
// every attempt fails do we fall back to whatever is in the local cache.
// A genuine empty server result is returned as-is, while a total failure
// throws so callers can distinguish "no levels" from "could not load levels".
async function fetchLevelsSnapshot(q: Query): Promise<QuerySnapshot> {
  try {
    return await retryAsync(() => getDocsFromServer(q), {
      attempts: LEVELS_FETCH_MAX_ATTEMPTS,
    });
  } catch (serverError) {
    try {
      const cachedSnapshot = await getDocs(q);
      if (!cachedSnapshot.empty) {
        console.warn("Using cached levels after server fetch failed.");
        return cachedSnapshot;
      }
    } catch (cacheError) {
      console.warn("Cached levels fetch also failed:", cacheError);
    }

    throw new Error(
      `Failed to fetch levels after ${LEVELS_FETCH_MAX_ATTEMPTS} server attempts: ${String(
        serverError,
      )}`,
    );
  }
}

export async function getLevelsForAppID(appID: string): Promise<Level[]> {
  const levelsRef = collection(db, "levels");
  const q = query(levelsRef, where("AppID", "==", appID));

  const querySnapshot = await fetchLevelsSnapshot(q);

  const levels: Array<Level> = [];
  querySnapshot.forEach((doc) => {
    const data: QueryData = doc.data() as QueryData;
    const levelData: Level = { id: doc.id, ...data };

    // Remap questionLoopIds if it exists
    if (levelData.QuestionLoopIds) {
      // @ts-ignore
      levelData.QuestionLoopIds = Object.values(
        levelData.QuestionLoopIds,
        // @ts-ignore
      ).map((loop) => [...loop]);
    }

    levels.push(levelData);
  });

  return levels.sort((a, b) => a.TotalLevelIndex - b.TotalLevelIndex);
}

export async function getEducations() {
  try {
    const educationsRef = collection(db, "apps");
    const q = query(
      educationsRef,
      where("PublishedStatus", "in", ["published", "comingSoon", "expired"]),
    );
    const querySnapshot = await getDocs(q);

    const educations: Array<Education> = [];
    querySnapshot.forEach((doc) => {
      const educationData: EducationQueryData =
        doc.data() as EducationQueryData;
      const education: Education = { ID: doc.id, ...educationData };
      educations.push(education);
    });

    if (educations.length > 0) {
      const sortedEducations = educations.sort((a, b) =>
        a.Title.localeCompare(b.Title),
      );
      return sortedEducations;
    } else {
      console.warn(
        "[getEducations] No educations found in database - this might be expected if no courses are published yet",
      );
      return [];
    }
  } catch (error: any) {
    console.error("Error fetching educations:", error);

    // Enhance error with more specific information
    const enhancedError = new Error(getFirebaseErrorMessage(error));
    enhancedError.name = error?.name || "FirebaseError";

    // Re-throw the enhanced error so calling code can handle retries
    throw enhancedError;
  }
}

export async function getTheoryForID(theoryID: string) {
  try {
    const theoryRef = doc(db, "theories", theoryID);
    const theorySnap = await getDoc(theoryRef);

    if (theorySnap.exists()) {
      return theorySnap.data();
    } else {
      console.error("No such theory document!");
      return null;
    }
  } catch (error) {
    console.error("Error fetching theory:", error);
    return null;
  }
}

export type RolePlay = {
  ID: string;
  Title?: string;
  SceneDescription?: string;
  ContentText?: string;
  ContentImage?: string;
  ContentVideo?: string;
  ContentAudio?: string;
  ExampleAnswer?: string;
  Explanation?: string;
  TheoryIDs?: string[];
  UpdatedAt?: string;
};

export async function getRolePlaysForTheory(
  theoryId: string,
): Promise<RolePlay[]> {
  try {
    const col = collection(db, "roleplays");
    const q = query(col, where("TheoryIDs", "array-contains", theoryId));
    const snap = await getDocs(q);
    const res: RolePlay[] = [];
    snap.forEach((doc) => {
      const data: any = doc.data();
      res.push({ ID: doc.id, ...data });
    });
    // sort by UpdatedAt desc if available
    return res.sort((a, b) => {
      const ad = a.UpdatedAt ? Date.parse(a.UpdatedAt) : 0;
      const bd = b.UpdatedAt ? Date.parse(b.UpdatedAt) : 0;
      return bd - ad;
    });
  } catch (e) {
    console.error("Error fetching roleplays:", e);
    return [];
  }
}

export type BranchingStory = {
  ID: string;
  Title: string;
  Description?: string;
  ScenarioIntro: string;
  TheoryIDs: string[];
  StartNodeId: string;
  Nodes: Array<{
    id: string;
    type: "intro" | "section" | "ending";
    title?: string;
    content: string;
    question?: string;
    choices?: Array<{
      id: string;
      text: string;
      feedback: string;
      isCorrect: boolean;
      targetNodeId: string;
    }>;
    endingType?: "good" | "acceptable" | "bad";
    result?: string;
  }>;
  OptimalPath: Array<{
    nodeId: string;
    choiceId: string;
    summary: string;
  }>;
  ShowOptimalPath: boolean;
  AllowReplay: boolean;
  CreatedAt?: string;
  UpdatedAt?: string;
};

export async function getBranchingStoriesForTheory(
  theoryId: string,
): Promise<BranchingStory[]> {
  try {
    const col = collection(db, "branchingStories");
    const q = query(col, where("TheoryIDs", "array-contains", theoryId));
    const snap = await getDocs(q);

    const res: BranchingStory[] = [];
    snap.forEach((docSnap) => {
      const data: any = docSnap.data();
      res.push({ ID: docSnap.id, ...data });
    });

    const sorted = res.sort((a, b) => {
      const ad = a.UpdatedAt ? Date.parse(a.UpdatedAt) : 0;
      const bd = b.UpdatedAt ? Date.parse(b.UpdatedAt) : 0;
      return bd - ad;
    });

    return sorted;
  } catch (e) {
    console.error(
      "[getBranchingStoriesForTheory] Error fetching branching stories:",
      e,
    );
    return [];
  }
}

export async function getObjectsForIDs(objectClass: any, ids: any) {
  try {
    // Filter out any invalid IDs
    const validIds = ids.filter((id: any) => id && typeof id === "string");

    if (validIds.length === 0) {
      return [];
    }

    // Create an array of promises for parallel fetching
    const fetchPromises = validIds.map(async (id: string) => {
      try {
        const ref = doc(db, objectClass, id);
        const snap = await getDoc(ref);

        if (snap.exists()) {
          return { ID: snap.id, ...snap.data() };
        } else {
          console.warn(
            `[getObjectsForIDs] Document not found: ${objectClass}/${id}`,
          );
          return null;
        }
      } catch (error) {
        console.error(
          `[getObjectsForIDs] Error fetching ${objectClass}/${id}:`,
          error,
        );
        return null;
      }
    });

    // Execute all fetches in parallel
    const results = await Promise.all(fetchPromises);

    // Filter out any null results from failed fetches
    const objects = results.filter(
      (obj): obj is NonNullable<typeof obj> => obj !== null,
    );

    return objects;
  } catch (error) {
    return [];
  }
}

export async function getQuestionsCountForTheory(
  theoryId: string,
): Promise<number> {
  try {
    const questionsRef = collection(db, "questions");
    const q = query(
      questionsRef,
      where("TheorySections", "array-contains", theoryId),
      where("Published", "==", true),
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    console.error("Error getting questions count for theory:", error);
    return 0;
  }
}

export async function getQuestionsForTheory(theoryId: string): Promise<any[]> {
  try {
    // Import cache utilities dynamically to avoid circular dependencies
    const { getCachedQuestions, setCachedQuestions } =
      await import("../util/questionCache");

    // Try to get from cache first
    const cachedQuestions = getCachedQuestions(theoryId);
    if (cachedQuestions) {
      return cachedQuestions;
    }

    // If not in cache, fetch from Firebase
    const questionsRef = collection(db, "questions");
    const q = query(
      questionsRef,
      where("TheorySections", "array-contains", theoryId),
      where("Published", "==", true),
    );
    const querySnapshot = await getDocs(q);
    const questions = querySnapshot.docs.map((doc) => ({
      ...doc.data(),
      ID: doc.id,
    }));

    // Cache the results
    setCachedQuestions(theoryId, questions);

    return questions;
  } catch (error) {
    console.error("Error getting questions for theory:", error);
    return [];
  }
}

async function excludeFillInBlankQuestionIds(
  questionIds: string[],
): Promise<string[]> {
  const unique = [...new Set(questionIds)];
  if (unique.length === 0) return [];

  const questionsRef = collection(db, "questions");
  const kept: string[] = [];
  const chunkSize = 10;

  for (let i = 0; i < unique.length; i += chunkSize) {
    const chunk = unique.slice(i, i + chunkSize);
    const q = query(questionsRef, where(documentId(), "in", chunk));
    const snap = await getDocs(q);
    snap.forEach((docSnap) => {
      const data = docSnap.data() as { QuestionType?: unknown };
      if (!isFillInBlankQuestionType(data?.QuestionType)) {
        kept.push(docSnap.id);
      }
    });
  }

  return kept;
}

export async function getQuizQuestionsFor(chapterIndex: any, appID: string) {
  try {
    const levelsRef = collection(db, "levels");
    const q = query(
      levelsRef,
      where("AppID", "==", appID),
      where("ChapterIndex", "==", chapterIndex),
    );
    const querySnapshot = await getDocs(q);

    // First, try the old logic: collect QuestionIDs and QuestionLoopIds
    let questionIds: Array<string> = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.LevelType !== "questionsCaseStudy") {
        // Exclude levels with LevelType "caseStudy"
        if (data.QuestionIDs) {
          questionIds = questionIds.concat(data.QuestionIDs);
        }
        if (data.QuestionLoopIds) {
          Object.values(data.QuestionLoopIds).forEach((loop) => {
            // @ts-ignore
            questionIds = questionIds.concat(loop);
          });
        }
      }
    });

    // If no questions found with old logic, try the new logic
    if (questionIds.length === 0) {
      // Filter to only theory levels and collect their IDs
      const theoryLevelIds: string[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.LevelType === "theory") {
          // Remove "theory" suffix from level ID
          const levelId = doc.id.endsWith("theory")
            ? doc.id.slice(0, -6)
            : doc.id;
          theoryLevelIds.push(levelId);
        }
      });

      if (theoryLevelIds.length === 0) {
        return [];
      }

      // Now fetch all questions that have TheorySections matching the theory level IDs
      const questionsRef = collection(db, "questions");
      const questionsQuery = query(
        questionsRef,
        where("TheorySections", "array-contains-any", theoryLevelIds),
        where("Published", "==", true),
      );
      const questionsSnapshot = await getDocs(questionsQuery);

      questionsSnapshot.forEach((doc) => {
        questionIds.push(doc.id);
      });
    }

    questionIds = await excludeFillInBlankQuestionIds(questionIds);

    let questionsPerSubtest = 10;

    // 1) Highest priority: environment override (centralized in constants.ts)
    if (typeof QUESTIONS_PER_SUBTEST_OVERRIDE === "number") {
      questionsPerSubtest = QUESTIONS_PER_SUBTEST_OVERRIDE;
    } else {
      // 2) Fallback: per-education Firestore config
      try {
        const educationRef = doc(db, "apps", appID);
        const educationDoc = await getDoc(educationRef);
        const configured = educationDoc.data()?.QuestionsPerSubtest as
          | number
          | string
          | undefined;
        if (configured != null) {
          const numeric =
            typeof configured === "string"
              ? parseInt(configured, 10)
              : Number(configured);
          if (!Number.isNaN(numeric) && Number.isFinite(numeric)) {
            questionsPerSubtest = Math.min(100, Math.max(1, numeric));
          }
        }
      } catch (e) {
        // Fallback silently to default 10 if reading config fails
      }
    }

    // Shuffle and select N random questionIds
    const shuffledQuestionIds = questionIds.sort(() => 0.5 - Math.random());
    return shuffledQuestionIds.slice(0, questionsPerSubtest);
  } catch (error) {
    console.error("Error fetching quiz questions:", error);
    return [];
  }
}

export async function getDocumentsForAppID(
  appID: string,
): Promise<EducationDocument[]> {
  try {
    const documentsRef = collection(db, "documents");
    const q = query(
      documentsRef,
      where("AppID", "==", appID),
      where("Published", "==", true),
    );
    const querySnapshot = await getDocs(q);
    const documents: EducationDocument[] = [];
    querySnapshot.forEach((docSnap) => {
      documents.push({
        ID: docSnap.id,
        ...docSnap.data(),
      } as EducationDocument);
    });

    // Sort by Index, then by Title
    documents.sort((a, b) => {
      const aIndex = a.Index ?? Number.MAX_SAFE_INTEGER;
      const bIndex = b.Index ?? Number.MAX_SAFE_INTEGER;
      if (aIndex !== bIndex) return aIndex - bIndex;
      return (a.Title || "").localeCompare(b.Title || "");
    });

    return documents;
  } catch (error) {
    console.error("Error fetching documents:", error);
    return [];
  }
}

export async function incrementDocumentDownload(
  documentId: string,
): Promise<void> {
  try {
    const docRef = doc(db, "documents", documentId);
    await updateDoc(docRef, {
      DownloadCount: increment(1),
    });
  } catch (error) {
    console.error("Error incrementing document download count:", error);
    throw error;
  }
}

// User
export async function saveAIChatChoiceStatistics(
  choice: "AIChat" | "questionsLinearAnswerMode",
  courseId: string,
): Promise<void> {
  const saveAIChatChoiceStats = httpsCallable(
    functions,
    "saveAIChatChoiceStatistics",
  );

  try {
    await saveAIChatChoiceStats({
      choice,
      courseId,
    });
  } catch (error) {
    console.error("Error saving AI chat choice statistics:", error);
    // Don't throw error to avoid breaking the user experience
  }
}

export async function saveUserChoicePreference(
  choice: "AIChat" | "questionsLinearAnswerMode",
  courseId: string,
): Promise<void> {
  const saveUserPreference = httpsCallable(
    functions,
    "saveUserChoicePreference",
  );

  try {
    await saveUserPreference({
      choice,
      courseId,
    });
  } catch (error) {
    console.error("Error saving user choice preference:", error);
    // Don't throw error to avoid breaking the user experience
  }
}

export async function getUserChoicePreference(
  courseId: string,
): Promise<"AIChat" | "questionsLinearAnswerMode" | null> {
  const getUserPreference = httpsCallable(functions, "getUserChoicePreference");

  try {
    const result = await getUserPreference({ courseId });
    return (result.data as any)?.preferredMode || null;
  } catch (error) {
    console.error("Error getting user choice preference:", error);
    return null;
  }
}

export async function userDidCompleteLevel(
  highestLevelCompleted: number,
  courseId: string,
  completedLevel: number,
  questionAnswers: Array<{ questionId: string; isCorrect: boolean }>,
  diagnosticQuestions: { [theoryId: string]: string[] },
  startTime?: number,
  levelType?: string,
  levelId?: string,
) {
  try {
    const userDidCompleteLevelFunction = httpsCallable(
      functions,
      "userDidCompleteLevelWithLogic",
    );
    const result = await userDidCompleteLevelFunction({
      highestLevelCompleted,
      courseId,
      completedLevel,
      questionAnswers,
      diagnosticQuestions,
      startTime,
      levelType,
      levelId,
    });

    return result.data;
  } catch (error: any) {
    // Handle Firebase Functions specific errors
    if (error.code === "functions/invalid-argument") {
      console.error("Invalid arguments provided:", error.message);
      throw new Error(`Failed to complete level: ${error.message}`);
    } else if (error.code === "functions/unauthenticated") {
      console.error("Authentication required:", error.message);
      throw new Error("You need to be logged in to complete levels");
    } else if (error.code === "functions/permission-denied") {
      console.error("Permission denied:", error.message);
      throw new Error("You do not have permission to complete this level");
    } else {
      console.error("Failed to update user education data:", error);
      throw new Error(
        error.message ||
          "An unexpected error occurred while completing the level",
      );
    }
  }
}

export async function fetchUserData(): Promise<UserData | null> {
  try {
    const getUserData = httpsCallable(functions, "getUserDataEUOnCall");
    const result = (await getUserData()) as { data: UserData };

    if (!result?.data) {
      console.warn("No user data returned from cloud function");
      return null;
    }

    return result.data;
  } catch (error: any) {
    console.error("Failed to fetch user data:", error);

    // Enhance error with more specific information
    const enhancedError = new Error(getFirebaseErrorMessage(error));
    enhancedError.name = error?.name || "FirebaseError";

    // Re-throw the enhanced error so calling code can handle retries
    throw enhancedError;
  }
}

export async function updateUserProfile(
  firstname?: string,
  lastname?: string,
): Promise<void> {
  try {
    const updateProfile = httpsCallable(functions, "updateUserProfile");
    await updateProfile({ firstname, lastname });
  } catch (error: any) {
    console.error("Failed to update user profile:", error);

    // Enhance error with more specific information
    const enhancedError = new Error(getFirebaseErrorMessage(error));
    enhancedError.name = error?.name || "FirebaseError";

    // Re-throw the enhanced error so calling code can handle retries
    throw enhancedError;
  }
}

export async function fetchUserEducations() {
  try {
    const userData: UserData | null = await fetchUserData();

    if (!userData) {
      console.warn("No userData found - user might not be logged in");
      return [];
    }

    if (!Array.isArray(userData.Educations)) {
      console.warn("No Educations array found in userData:", userData);
      return [];
    }

    return userData.Educations;
  } catch (error: any) {
    console.error("Failed to fetch user educations:", error);

    // Enhance error with more specific information
    const enhancedError = new Error(getFirebaseErrorMessage(error));
    enhancedError.name = error?.name || "FirebaseError";

    // Re-throw the enhanced error so calling code can handle retries
    throw enhancedError;
  }
}

export async function fetchCertificatesFromFunction(courseId: string) {
  try {
    // @ts-ignore
    const userId = auth.currentUser.uid;

    const fetchUserCertificates = httpsCallable(
      functions,
      "getCertificateForUser",
    );

    // Note: Authentication is handled automatically by Firebase httpsCallable
    // via the user's auth state. No need to pass token manually.
    const response = await fetchUserCertificates({ userId, courseId });

    if (response?.data) {
      return response.data;
    } else {
      console.error("Unexpected response:", response);
      alert("Något gick fel, försök igen senare.");
      return undefined;
    }
  } catch (error) {
    console.error("Error fetching certificates from cloud function:", error);
    return [];
  }
}

/**
 * @param {string} courseId
 */
export async function fetchCombinedCertificate(courseId: string) {
  try {
    // @ts-ignore
    const userId = auth.currentUser.uid;

    const fetchCombinedCertificate = httpsCallable(
      functions,
      "fetchCombinedCertificate",
    );

    // Note: Authentication is handled automatically by Firebase httpsCallable
    // via the user's auth state. No need to pass token manually.
    const response = await fetchCombinedCertificate({
      userId,
      educationId: courseId,
    });

    if (response?.data) {
      const data = response.data as
        | { pdf?: string; fileName?: string }
        | string;
      const pdfBase64 = typeof data === "string" ? data : data?.pdf;
      let fileName: string;
      if (typeof data === "object" && data?.fileName) {
        fileName = data.fileName;
      } else {
        try {
          const { default: i18next } = await import("i18next");
          fileName = i18next.t("certificates.downloadFilename");
        } catch {
          fileName = "Education summary.pdf";
        }
      }

      if (!pdfBase64) {
        console.error("Unexpected response: no PDF data");
        return false;
      }

      const cleanBase64 = String(pdfBase64).replace(/\s/g, "").trim();
      const byteCharacters = atob(cleanBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      const safeFileName =
        fileName.replace(/[/\\:*?"<>|]/g, "-") || "certifikat.pdf";
      saveBlobAsFile(blob, safeFileName);

      return true;
    } else {
      console.error("Unexpected response:", response);
      try {
        const { default: i18next } = await import("i18next");
        alert(i18next.t("common.errorTryAgain"));
      } catch {
        alert("Something went wrong, please try again later.");
      }
      return false;
    }
  } catch (error) {
    console.error("Error fetching certificates from cloud function:", error);
    return false;
  }
}

/**
 * Downloads a certificate PDF via the storage proxy callable. Use this when the
 * signed-in user must read another user's object under certificate/{uid}/...
 * (e.g. company admin in the employee table). Direct Storage client access is
 * denied by rules for non-owners.
 */
export async function downloadCertificateThroughStorageProxy(
  gsUri: string,
  preferredFileName?: string,
): Promise<boolean> {
  try {
    const storageProxy = httpsCallable(functions, "storageProxyOnCall");
    const response = await storageProxy({ gsUri });
    const payload = response?.data as
      | { contentType?: string; data?: string }
      | undefined;
    const base64 = payload?.data;
    if (!base64) {
      console.error("Unexpected response from storageProxyOnCall:", response);
      return false;
    }

    const cleanBase64 = String(base64).replace(/\s/g, "").trim();
    const byteCharacters = atob(cleanBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const contentType = payload.contentType || "application/pdf";
    const blob = new Blob([byteArray], { type: contentType });

    const rawName =
      preferredFileName?.trim() ||
      gsUri.split("/").pop()?.split("?")[0] ||
      "certificate.pdf";
    const safeFileName = rawName.replace(/[/\\:*?"<>|]/g, "-");

    saveBlobAsFile(blob, safeFileName);

    return true;
  } catch (error) {
    console.error("Error downloading certificate via storage proxy:", error);
    return false;
  }
}

export async function hasUserSubmittedCourseReview(courseId: string) {
  try {
    const userId = auth.currentUser?.uid;
    if (userId == null) {
      return false;
    }

    const checkReviewFunction = httpsCallable(
      functions,
      "hasUserSubmittedCourseReviewForCourse",
    );

    const response = await checkReviewFunction({
      UserId: userId,
      CourseId: courseId,
    });

    // @ts-ignore
    const hasLeftReview = response?.data?.hasReview || false;
    return hasLeftReview;
  } catch (error) {
    console.error("Error checking if user submitted course review:", error);
    return false;
  }
}

export async function getEmployees(groupId: string) {
  try {
    if (!auth.currentUser) {
      throw new Error("User is not authenticated.");
    }

    const listEmployeesFunction = httpsCallable(
      functions,
      "listEmployeesAndEducations",
    );

    const response = await listEmployeesFunction({ groupId });

    if (response?.data) {
      return response.data;
    } else {
      console.error("Unexpected response:", response);
      return [];
    }
  } catch (error) {
    console.error("Error fetching employees:", error);
    throw error;
  }
}

/**
 * Filters the AI response by removing unwanted parts.
 * It removes the "[DONE]" and "[COMPLETED]" markers and
 * any text that follows "[COMPLETED]".
 * It also trims the response to remove leading and trailing whitespace.
 *
 * @param {string} response - The AI response to filter.
 * @returns {string} - The filtered response.
 */
function filterAIResponse(response: string): string {
  return response
    .replace("[DONE]", "")
    .replace(/\[COMPLETED\].*/g, "\n")
    .trim();
}

/**
 * Makes a request to the OpenAI API to get a response based on the
 * provided messagesand study text.
 * The response is streamed in chunks and can be processed using the
 * provided onChunk callback.
 *
 * Returns a promise that resolves to true if the response contains
 * "[COMPLETED]".
 *
 * @param {Object} params - The parameters for the request.
 *
 * @param {Function} onChunk - A callback function to process each
 *                             chunk of the response.
 * @returns - A promise that resolves to true if the
 *            response contains "[COMPLETED]".
 */
export async function fetchAIResponse(
  params: AIParameters,
  onChunk?: Function,
): Promise<AIResponse> {
  const { messages, studyText, aiChatSummary, educationId, theoryId, chatType } =
    params;

  // Get Firebase ID token for authentication
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new Error("User not authenticated");
  }

  const requestBody: Record<string, unknown> = {
    messages,
    studyText,
    aiChatSummary,
    educationId,
    chatType: chatType || "smartChat", // Default to smartChat for backward compatibility
  };
  if (theoryId != null && theoryId !== "") {
    requestBody.theoryId = theoryId;
  }

  const response = await fetch(
    "https://europe-west1-finans-utbildarna.cloudfunctions.net/getOpenAIResponse3",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    },
  );

  // Check for HTTP errors
  if (!response.ok) {
    console.error(
      `AI response failed with status ${response.status}: ${response.statusText}`,
    );
    throw new Error(`AI request failed: ${response.statusText}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("Failed to get response stream");
  }

  const decoder = new TextDecoder("utf-8");
  let fullResponse = "";
  let done = false;
  let firstChunkReceived = false;

  while (!done) {
    const { value, done: doneReading } = await reader.read();
    done = doneReading;
    const chunkValue = decoder.decode(value);

    if (!firstChunkReceived && chunkValue) {
      firstChunkReceived = true;
    }

    fullResponse += chunkValue;

    // Check if backend reported an error (all AI models failed)
    if (fullResponse.includes("[ERROR]")) {
      console.error("AI backend reported error - all models failed");
      throw new Error("AI service temporarily unavailable");
    }

    onChunk?.(filterAIResponse(fullResponse), firstChunkReceived);
  }

  // Check if we received an empty response
  const filteredResponse = filterAIResponse(fullResponse);
  if (!filteredResponse || filteredResponse.trim().length === 0) {
    console.error("AI returned empty response");
    throw new Error("AI returned empty response");
  }

  return {
    completed: fullResponse.includes("[COMPLETED]"),
    data: filteredResponse,
  };
}

export async function getTheoryInfoForDiagnostic(
  theoryIds: string[],
): Promise<TheoryInfo[]> {
  try {
    if (!Array.isArray(theoryIds) || theoryIds.length === 0) {
      return [];
    }

    const validIds = theoryIds.filter((id) => id && typeof id === "string");
    if (validIds.length === 0) return [];

    const fetchPromises = validIds.map(async (theoryId) => {
      try {
        const theoryRef = doc(db, "theories", theoryId);
        const theorySnap = await getDoc(theoryRef);

        if (!theorySnap.exists()) {
          return {
            id: theoryId,
            name: "Unknown Theory",
            description: "",
          };
        }

        const data = theorySnap.data();
        return {
          id: theoryId,
          name: data?.Name || "Unknown Theory",
          category: data?.Category || "",
          sectionTitle: data?.SectionTitle || "",
          description: data?.Description || "",
        };
      } catch {
        return {
          id: theoryId,
          name: "Unknown Theory",
          description: "",
        };
      }
    });

    return await Promise.all(fetchPromises);
  } catch (error) {
    return [];
  }
}

export async function getEducationById(
  educationId: string,
): Promise<Education | null> {
  try {
    const educationRef = doc(db, "apps", educationId);
    const educationDoc = await getDoc(educationRef);

    if (!educationDoc.exists()) {
      console.error("No education found with the given ID!");
      return null;
    }

    const educationData: EducationQueryData =
      educationDoc.data() as EducationQueryData;
    return { ID: educationDoc.id, ...educationData };
  } catch (error) {
    console.error("Error fetching education:", error);
    return null;
  }
}

// Practice Helpers
export async function getTheoryCategoriesForApp(
  appID: string,
): Promise<string[]> {
  try {
    const levels = await getLevelsForAppID(appID);
    const theoryIds = levels
      .filter((lvl: any) => lvl.LevelType === "theory" && lvl.TheoryID)
      .map((lvl: any) => String(lvl.TheoryID));

    const theories = await getObjectsForIDs("theories", theoryIds);
    const categories = Array.from(
      new Set(
        theories
          .map((t: any) => t?.Category)
          .filter((c: any) => typeof c === "string" && c.trim() !== ""),
      ),
    ) as string[];
    return categories.sort((a, b) => a.localeCompare(b));
  } catch (e) {
    return [];
  }
}

export async function getQuestionsForCategories(
  appID: string,
  categories: string[],
  limit: number = QUESTIONS_PER_SUBTEST_OVERRIDE ?? 10,
): Promise<Question[]> {
  try {
    // 1) Get all theory levels for app
    const levelsRef = collection(db, "levels");
    const q = query(levelsRef, where("AppID", "==", appID));
    const levelSnapshot = await getDocs(q);

    const theoryIdToLevelSection: Record<string, string> = {};
    levelSnapshot.forEach((d) => {
      const data = d.data();
      if (data.LevelType === "theory" && data.TheoryID) {
        const levelId = d.id.endsWith("theory") ? d.id.slice(0, -6) : d.id;
        theoryIdToLevelSection[data.TheoryID] = levelId;
      }
    });

    // 2) Fetch theories for these IDs and filter by categories
    const theoryIds = Object.keys(theoryIdToLevelSection);
    const theories = await getObjectsForIDs("theories", theoryIds);
    const selectedLevelSections: string[] = theories
      .filter((t: any) =>
        categories.length === 0
          ? true
          : categories.includes(String(t?.Category ?? "")),
      )
      .map((t: any) => theoryIdToLevelSection[t.ID])
      .filter(Boolean);

    if (selectedLevelSections.length === 0) {
      return [];
    }

    // 3) Firestore array-contains-any supports max 10 items; chunk queries
    const chunkSize = 10;
    const chunks: string[][] = [];
    for (let i = 0; i < selectedLevelSections.length; i += chunkSize) {
      chunks.push(selectedLevelSections.slice(i, i + chunkSize));
    }

    const questionIds: string[] = [];
    for (const chunk of chunks) {
      const questionsRef = collection(db, "questions");
      const questionsQuery = query(
        questionsRef,
        where("TheorySections", "array-contains-any", chunk),
        where("Published", "==", true),
      );
      const questionsSnapshot = await getDocs(questionsQuery);
      questionsSnapshot.forEach((doc) => questionIds.push(doc.id));
    }

    if (questionIds.length === 0) return [];

    // 4) Shuffle and pick N
    const shuffled = questionIds.sort(() => 0.5 - Math.random());
    const picked = shuffled.slice(0, limit);
    const questions = await getObjectsForIDs("questions", picked);
    return questions as Question[];
  } catch (e) {
    return [];
  }
}

/**
 * Converts Firebase error to user-friendly message
 */
function getFirebaseErrorMessage(error: any): string {
  if (error?.code === "functions/internal") {
    return "Serverfel - försök igen senare";
  } else if (error?.code === "functions/unauthenticated") {
    return "Du behöver logga in igen";
  } else if (error?.code === "functions/permission-denied") {
    return "Du har inte behörighet att komma åt denna information";
  } else if (error?.code === "functions/unavailable") {
    return "Tjänsten är tillfälligt otillgänglig - försök igen senare";
  } else if (error?.code === "functions/deadline-exceeded") {
    return "Begäran tog för lång tid - försök igen";
  } else if (error?.code === "functions/resource-exhausted") {
    return "Tjänsten är överbelastad - försök igen senare";
  } else if (error?.message?.includes("INTERNAL")) {
    return "Serverfel - försök igen senare";
  } else if (error?.message?.includes("Failed to fetch")) {
    return "Kontrollera din internetanslutning och försök igen";
  } else if (error?.message?.includes("NetworkError")) {
    return "Nätverksfel - kontrollera din internetanslutning";
  } else if (error?.message?.includes("timeout")) {
    return "Begäran tog för lång tid - försök igen";
  } else {
    return "Ett oväntat fel uppstod - försök igen senare";
  }
}

/**
 * Fetches documents from a specific collection using cloud function
 * @param className - The collection name to query
 * @param appId - The app ID to filter by
 * @returns Promise with documents array
 */
export async function getClassDocumentsFromCloud(
  className: string,
  appId: string,
): Promise<any[]> {
  try {
    const getClassDocuments = httpsCallable(functions, "getClassDocuments");

    const response = await getClassDocuments({
      className,
      appId,
    });

    return (response.data as any)?.documents || [];
  } catch (error) {
    console.error("Error fetching class documents:", error);
    throw error;
  }
}

/**
 * Fetches published apps using cloud function
 * @returns Promise with apps array
 */
export async function getAppsFromCloud(): Promise<any[]> {
  try {
    const getApps = httpsCallable(functions, "getApps");

    const response = await getApps({});

    return (response.data as any)?.documents || [];
  } catch (error) {
    console.error("Error fetching apps:", error);
    throw error;
  }
}

/**
 * Fetches levels for a specific app using cloud function
 * @param appId - The app ID to get levels for
 * @param includeMasterEndLevel - Whether to include master end level
 * @returns Promise with levels array
 */
export async function getLevelsForAppIDFromCloud(
  appId: string,
  includeMasterEndLevel: boolean = false,
): Promise<any[]> {
  try {
    const getLevelsForAppID = httpsCallable(functions, "getLevelsForAppID");

    const response = await getLevelsForAppID({
      appId,
      includeMasterEndLevel,
    });

    return (response.data as any)?.levels || [];
  } catch (error) {
    console.error("Error fetching levels for app:", error);
    throw error;
  }
}

/**
 * Saves an AI chat conversation to the logs collection for analysis.
 * @param messages - Array of message objects with role and content
 * @param level - The level object containing course and education info
 * @param status - Status of the conversation: "completed", "aborted", or "switchedToQuestions"
 * @param chatType - Type of chat: "smartChat" or "roleplay"
 * @returns Promise with the result including AI analysis
 */
export async function saveAIChatConversation(
  messages: Array<{ role: string; content: string }>,
  level: any,
  status: "completed" | "aborted" | "switchedToQuestions",
  chatType: "smartChat" | "roleplay" | "theoryQ&A" = "smartChat",
): Promise<any> {
  try {
    // Get Firebase ID token for authentication
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    const courseId = level.CourseID || level.AppID;
    const educationId = level.AppID;
    const levelId = level.ID || level.LevelID || null;

    const response = await fetch(
      "https://europe-west1-finans-utbildarna.cloudfunctions.net/saveAIChatConversation",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          messages,
          courseId,
          educationId,
          levelId,
          status,
          chatType,
        }),
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error saving AI chat conversation:", error);
    throw error;
  }
}

/**
 * Logs when a user shares their certificate on LinkedIn
 * @param courseId - The ID of the course for which the certificate is being shared
 * @returns Promise with the result including log ID
 */
export async function logCertificateShare(courseId: string): Promise<any> {
  try {
    const logCertificateShareFunction = httpsCallable(
      functions,
      "logCertificateShare",
    );
    const result = await logCertificateShareFunction({ courseId });
    return result.data;
  } catch (error) {
    console.error("Error logging certificate share:", error);
    throw error;
  }
}

/**
 * Logs when a user clicks the LinkedIn follow button
 * @param location - The location where the button was clicked (e.g., "sidebar", "certifikat")
 * @returns Promise with the result including log ID
 */
export async function logTheoryTTSListen(
  courseId: string,
  levelId: string,
  theoryId: string,
): Promise<void> {
  try {
    console.log("[TTS] Calling logTheoryTTSListen cloud function", { courseId, levelId, theoryId });
    const logFn = httpsCallable(functions, "logTheoryTTSListen");
    const result = await logFn({ courseId, levelId, theoryId });
    console.log("[TTS] logTheoryTTSListen result:", result.data);
  } catch (error) {
    console.error("[TTS] logTheoryTTSListen failed:", error);
  }
}

export async function logLinkedInFollowClick(location?: string): Promise<any> {
  try {
    const logLinkedInFollowClickFunction = httpsCallable(
      functions,
      "logLinkedInFollowClick",
    );
    const result = await logLinkedInFollowClickFunction({ location });
    return result.data;
  } catch (error) {
    console.error("Error logging LinkedIn follow click:", error);
    throw error;
  }
}

export type CertificateRecommendationClickKind = "recommendation" | "catalog";

export async function logCertificateRecommendationClick(params: {
  clickKind: CertificateRecommendationClickKind;
  recommendationId?: string;
  recommendationTitle?: string;
  destinationUrl: string;
}): Promise<void> {
  try {
    const logFn = httpsCallable(functions, "logCertificateRecommendationClick");
    await logFn(params);
  } catch (error) {
    console.error("Error logging certificate recommendation click:", error);
  }
}
